import { createApi } from '@reduxjs/toolkit/query/react';
import { BenefitType, ReferentPriceType, USState } from '@strata/constants';
import { sortAsc } from '@strata/helper-functions';

import { AUCTION_API_URL } from 'api/APIBase';
import { PrescriberListPrescriber } from 'api/auctionsAPI';
import { handleRTKQueryError } from 'services/Error';
import { getBaseQuery } from 'store/api/apiBaseQuery';

export interface LiveReferralHours {
    start: number;
    end: number;
    lastStart: number;
}

export interface LostReferral {
    referralId: string;
    payerId: string;
    segmentId: string;
    productServiceOid: string;
    product: string;
    strength: string;
    qty: number;
    bidAmt: string;
    auctionEnd: string;
    isBrand: boolean;
    benefit: BenefitType;
    referentPriceType: ReferentPriceType;
}

export interface PrescriberList {
    prescriberListId: string;
    listName: string;
    name: string;
    lastUpdated: string;
    updatedBy: string;
    prescribers: PrescriberListPrescriber[];
}

export interface StateList {
    stateListId: string;
    listName: string;
    name: string;
    lastUpdated: string;
    updatedBy: string;
    states: USState[];
}

export const auctionApi = createApi({
    baseQuery: getBaseQuery(AUCTION_API_URL, true),
    reducerPath: 'auctionApi',
    endpoints: (build) => ({
        getLiveReferralHours: build.query<LiveReferralHours, void>({
            query: () => 'hours',
            transformResponse: (response: { start: number; end: number; last_start: number }) => ({
                start: response.start,
                end: response.end,
                lastStart: response.last_start,
            }),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving Live Referral hours.',
            ),
        }),
        getLostReferrals: build.query<LostReferral[], void>({
            query: () => 'lost',
            transformResponse: (
                response: {
                    referral_id: string;
                    payer_id: string;
                    segment_id: string;
                    product_service_oid: string;
                    product_service_description: string;
                    strength: string;
                    product_qty: string;
                    bid_amt: string;
                    auction_end: string;
                    is_brand: boolean;
                    benefit: BenefitType;
                    referent_price_type: ReferentPriceType;
                }[],
            ) =>
                response.map((referral) => ({
                    referralId: referral.referral_id,
                    product: referral.product_service_description,
                    strength: referral.strength,
                    qty: Number(referral.product_qty),
                    bidAmt: referral.bid_amt,
                    auctionEnd: referral.auction_end,
                    isBrand: referral.is_brand,
                    // Needed for product config
                    productServiceOid: referral.product_service_oid,
                    payerId: referral.payer_id,
                    segmentId: referral.segment_id,
                    benefit: referral.benefit.trim() as BenefitType,
                    referentPriceType: referral.referent_price_type,
                })),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving lost referrals.',
            ),
        }),
        getPrescriberLists: build.query<PrescriberList[], void>({
            query: () => 'prescriber_lists/summary',
            transformResponse: (
                response: {
                    prescriber_list_id: string;
                    prescriber_list_name: string;
                    last_updated: string;
                    updated_by: string;
                    prescriber_npis: { npi: string; name: string }[];
                }[],
            ) =>
                (response ?? [])
                    .map((list) => ({
                        prescriberListId: list.prescriber_list_id,
                        listName: list.prescriber_list_name,
                        name: list.prescriber_list_name,
                        lastUpdated: list.last_updated,
                        updatedBy: list.updated_by,
                        prescribers: list.prescriber_npis,
                    }))
                    .sort((a, b) => sortAsc(a.listName, b.listName)),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving prescriber lists.',
            ),
        }),
        getStateLists: build.query<StateList[], void>({
            query: () => 'state_lists/summary',
            transformResponse: (
                response: {
                    state_list_id: string;
                    state_list_name: string;
                    last_updated: string;
                    updated_by: string;
                    states: USState[];
                }[],
            ) =>
                (response ?? [])
                    .map((list) => ({
                        stateListId: list.state_list_id,
                        listName: list.state_list_name,
                        name: list.state_list_name,
                        lastUpdated: list.last_updated,
                        updatedBy: list.updated_by,
                        states: list.states,
                    }))
                    .sort((a, b) => sortAsc(a.listName, b.listName)),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving state lists.',
            ),
        }),
    }),
});

export const {
    useGetLiveReferralHoursQuery,
    useGetLostReferralsQuery,
    useGetPrescriberListsQuery,
    useGetStateListsQuery,
} = auctionApi;
